'use client';

import { cn } from '../../../lib/utils';
import type { RankedAnimal } from '../types';
import { MatchPercentBadge } from './MatchPercentBadge';
import {
  ShareToSocialsPanel,
  type ShareChannelHandlers,
  type ShareOverlayPalette,
} from './ShareToSocialsPanel';

export type MatchShareQuizResultsListSlideProps = ShareChannelHandlers & {
  sortedMatches: RankedAnimal[];
  emailShareDisabled?: boolean;
  palette?: ShareOverlayPalette;
  className?: string;
};

function PetThumb({
  animal,
  alt,
}: {
  animal: RankedAnimal['animal'];
  alt: string;
}) {
  if (animal.CoverPhoto) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- shelter CDN URLs
      <img
        src={animal.CoverPhoto}
        alt={alt}
        className='size-full object-cover'
      />
    );
  }
  return (
    <div className='flex size-full items-center justify-center bg-accent text-xs text-muted-foreground'>
      —
    </div>
  );
}

export function MatchShareQuizResultsListSlide({
  sortedMatches,
  emailShareDisabled = false,
  palette = 'default',
  className,
  ...shareHandlers
}: MatchShareQuizResultsListSlideProps) {
  const dark = palette === 'figmaDark';

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-6',
        className,
      )}
    >
      <div className='order-2 flex min-h-0 min-w-0 flex-1 flex-col lg:order-1'>
        <ul
          className={cn(
            'grid gap-3',
            dark
              ? 'grid-cols-1 overflow-visible lg:max-h-[min(70vh,40rem)] lg:grid-cols-2 lg:gap-4 lg:overflow-y-auto'
              : 'max-h-[min(55vh,28rem)] grid-cols-1 overflow-y-auto sm:max-h-[min(60vh,32rem)] sm:grid-cols-2 sm:gap-4 lg:max-h-[min(70vh,40rem)] lg:gap-4',
          )}
        >
          {sortedMatches.map((item, index) => {
            const rank = index + 1;
            const { animal, matchPercent } = item;
            return (
              <li
                key={animal.ID}
                className={cn(
                  'flex gap-3 rounded-xl border p-3 shadow-sm',
                  dark
                    ? 'border-white/10 bg-white/5'
                    : 'border-border bg-card',
                )}
              >
                <div
                  className={cn(
                    'shrink-0 overflow-hidden rounded-lg border',
                    dark
                      ? 'size-20 border-white/10 sm:size-24'
                      : 'size-16 border-border sm:size-20',
                  )}
                >
                  <PetThumb animal={animal} alt={animal.Name} />
                </div>
                <div className='min-w-0 flex-1'>
                  <p
                    className={cn(
                      'text-sm font-bold',
                      dark ? 'text-primary-400' : 'text-primary-600',
                    )}
                  >
                    #{rank}
                  </p>
                  <p
                    className={cn(
                      'font-heading text-base font-semibold',
                      dark ? 'text-white' : 'text-foreground',
                    )}
                  >
                    {animal.Name}
                  </p>
                  <div className='mt-2'>
                    <MatchPercentBadge matchPercent={matchPercent} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className='order-1 shrink-0 lg:order-2 lg:w-64 xl:w-72'>
        <div className='lg:hidden'>
          <ShareToSocialsPanel
            layout='row'
            palette={palette}
            emailDisabled={emailShareDisabled}
            {...shareHandlers}
          />
        </div>
        <div className='hidden lg:block'>
          <ShareToSocialsPanel
            layout='column'
            palette={palette}
            emailDisabled={emailShareDisabled}
            {...shareHandlers}
          />
        </div>
      </div>
    </div>
  );
}
